import { parsePreviewError } from '../utils/errorParser';

export interface PreviewJobLabels {
  headline: string;
  divider: string;
  tagline: string;
}

export interface PreviewJobParams {
  address: string;
  boundingBox: [number, number, number, number]; // [minLng, minLat, maxLng, maxLat]
  configName: string;
  sizeId: string;
  paletteId: string;
  labels?: PreviewJobLabels;
}

export interface PreviewJobResponse {
  jobId: string;
  status: string;
}

export interface PreviewJobStatus {
  jobId: string;
  status: 'processing' | 'completed' | 'failed';
  currentStage?: number;
  stages?: Array<{
    id: number;
    status: 'pending' | 'active' | 'completed';
  }>;
  glbUrl?: string;
  stats?: {
    buildingCount?: number;
    coverageKm2?: number;
  };
  error_msg?: string;  // Legacy field
  error?: string;      // New field (backend returns both during transition)
}

// Frontend → Backend style ID mapping
const STYLE_MAP: Record<string, string> = {
  osm2world: 'osm2world',
  terrain_only: 'terrain_only',
};

// Frontend size (SKU) → Backend size ID mapping
const SIZE_MAP: Record<string, string> = {
  'citymap-xs': 'small_square',
  'citymap-small': 'medium_square',
  'citymap-medium': 'large_square',
  'citymap-large': 'xlarge_square',
  // Config IDs (used when frontend already uses backend-style IDs)
  xs: 'small_square',
  small: 'medium_square',
  medium: 'large_square',
  large: 'xlarge_square',
};

export function mapStyleToBackendId(frontendStyle: string): string | null {
  return STYLE_MAP[frontendStyle] ?? null;
}

export function mapSizeToBackendId(frontendSize: string): string | null {
  return SIZE_MAP[frontendSize] ?? SIZE_MAP[frontendSize.toLowerCase()] ?? null;
}


type StageStatus = 'pending' | 'active' | 'completed';

const DEFAULT_STAGES: Array<{ id: number; status: StageStatus }> = [
  { id: 1, status: 'pending' },
  { id: 2, status: 'pending' },
  { id: 3, status: 'pending' },
  { id: 4, status: 'pending' },
  { id: 5, status: 'pending' },
];

/**
 * Maps backend preview-jobs stages to frontend display format.
 * Backend v1.5.0 returns stages as Array<{id, status}> with 5 stages.
 */
export function mapPipelineStagesToFrontend(
  stages: Array<{ id: number; status: string }> | undefined
): Array<{ id: number; status: StageStatus }> {
  if (!stages || stages.length === 0) {
    return DEFAULT_STAGES;
  }

  return [1, 2, 3, 4, 5].map((id) => {
    const backendStage = stages.find((s) => s.id === id);
    if (!backendStage) return { id, status: 'pending' as StageStatus };

    const status = backendStage.status;
    if (status === 'completed') return { id, status: 'completed' as StageStatus };
    if (status === 'active') return { id, status: 'active' as StageStatus };
    return { id, status: 'pending' as StageStatus };
  });
}

export function isRetriableError(error: string): boolean {
  // Client-side errors — fast path (not from backend)
  const clientNonRetriable = [
    'Please select a location',
    'aborted',
    'cancelled',
    'WebGL',
    'Invalid product',
  ];
  if (clientNonRetriable.some((kw) =>
    error.toLowerCase().includes(kw.toLowerCase())
  )) {
    return false;
  }

  // Delegate to parsePreviewError for backend error classification
  return parsePreviewError(error).retriable;
}

function getApiBaseUrl(): string {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  throw new Error(
    'GeoForge3D API URL not configured. Set VITE_API_BASE_URL env var.'
  );
}

function getApiHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const apiKey = import.meta.env.VITE_API_KEY;
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }

  return headers;
}

export async function createPreviewJob(
  params: PreviewJobParams,
  signal?: AbortSignal
): Promise<PreviewJobResponse> {
  const res = await fetch(`${getApiBaseUrl()}/v1/preview-jobs`, {
    method: 'POST',
    headers: getApiHeaders(),
    body: JSON.stringify(params),
    signal,
  });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Failed to create preview job: ${res.status} ${body}`);
  }

  return res.json();
}

// Allowed origins for loading GLB preview files
const ALLOWED_GLB_ORIGINS = [
  'https://storage.googleapis.com',
  'https://storage.cloud.google.com',
];

export function isAllowedGlbOrigin(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ALLOWED_GLB_ORIGINS.includes(parsed.origin);
  } catch {
    return false;
  }
}

export async function getPreviewJobStatus(
  jobId: string,
  signal?: AbortSignal
): Promise<PreviewJobStatus> {
  if (!/^[a-zA-Z0-9_-]+$/.test(jobId)) {
    throw new Error('Invalid job ID format');
  }

  const res = await fetch(
    `${getApiBaseUrl()}/v1/preview-jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'GET',
      headers: getApiHeaders(),
      signal,
    }
  );

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Failed to get job status: ${res.status} ${body}`);
  }

  return res.json();
}
